import React, { useState, useEffect } from 'react';
import { Dropdown, Badge, Button, ListGroup, Card } from 'react-bootstrap';
import {
  FaBell,
  FaTicketAlt,
  FaUser,
  FaCheck,
  FaTrash,
  FaBellSlash,
} from 'react-icons/fa';
import WebSocketService from '../../services/WebSocketService';
import './NotificationCenter.scss';

function NotificationCenter() {
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [muted, setMuted] = useState(false);

  useEffect(() => {
    // Carregar notificações iniciais
    setNotifications(WebSocketService.getNotifications());
    setUnreadCount(WebSocketService.getUnreadCount());
    setMuted(WebSocketService.getMuted());

    // Listener para novas notificações
    const handleNewNotification = (notification) => {
      setNotifications(WebSocketService.getNotifications());
      setUnreadCount(WebSocketService.getUnreadCount());
    };

    // Listener para notificações lidas
    const handleNotificationRead = () => {
      setNotifications(WebSocketService.getNotifications());
      setUnreadCount(WebSocketService.getUnreadCount());
    };

    // Listener para todas as notificações lidas
    const handleAllNotificationsRead = () => {
      setNotifications(WebSocketService.getNotifications());
      setUnreadCount(0);
    };

    // Listener para notificações limpas
    const handleNotificationsCleared = () => {
      setNotifications([]);
      setUnreadCount(0);
    };

    // Listener para atualização de contagem de notificações (tickets + pré-tickets)
    const handleNotificationCountUpdate = (data) => {
      setUnreadCount(data.total);
    };

    const handleMuteUpdate = (data) => {
      setMuted(!!data?.muted);
    };

    WebSocketService.addEventListener('notification', handleNewNotification);
    WebSocketService.addEventListener(
      'notificationRead',
      handleNotificationRead
    );
    WebSocketService.addEventListener(
      'allNotificationsRead',
      handleAllNotificationsRead
    );
    WebSocketService.addEventListener(
      'notificationsCleared',
      handleNotificationsCleared
    );
    WebSocketService.addEventListener(
      'notificationCountUpdate',
      handleNotificationCountUpdate
    );
    WebSocketService.addEventListener(
      'notificationMuteUpdate',
      handleMuteUpdate
    );

    return () => {
      WebSocketService.removeEventListener(
        'notification',
        handleNewNotification
      );
      WebSocketService.removeEventListener(
        'notificationRead',
        handleNotificationRead
      );
      WebSocketService.removeEventListener(
        'allNotificationsRead',
        handleAllNotificationsRead
      );
      WebSocketService.removeEventListener(
        'notificationsCleared',
        handleNotificationsCleared
      );
      WebSocketService.removeEventListener(
        'notificationCountUpdate',
        handleNotificationCountUpdate
      );
      WebSocketService.removeEventListener(
        'notificationMuteUpdate',
        handleMuteUpdate
      );
    };
  }, []);

  const toggle = () => setIsOpen(!isOpen);

  const handleMarkAsRead = (notificationId) => {
    WebSocketService.markAsRead(notificationId);
  };

  const handleMarkAllAsRead = () => {
    WebSocketService.markAllAsRead();
  };

  const handleClearAll = () => {
    WebSocketService.clearNotifications();
  };

  const toggleMute = () => {
    WebSocketService.setMuted(!muted);
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'ticket_assignment':
        return <FaTicketAlt color="#007bff" />;
      case 'ticket_status_change':
        return <FaTicketAlt color="#ffc107" />;
      case 'ticket_open':
        return <FaTicketAlt color="#dc3545" />;
      case 'ticket_message':
        return <FaUser color="#17a2b8" />;
      case 'preticket_new':
        return <FaTicketAlt color="#28a745" />;
      case 'preticket_item':
        return <FaTicketAlt color="#17a2b8" />;
      default:
        return <FaBell color="#6c757d" />;
    }
  };

  const getNotificationColor = (type) => {
    switch (type) {
      case 'ticket_assignment':
        return '#007bff'; // Azul
      case 'ticket_status_change':
        return '#ffc107'; // Amarelo
      case 'ticket_open':
        return '#dc3545'; // Vermelho para tickets em aberto
      case 'ticket_message':
        return '#17a2b8'; // Azul claro
      case 'preticket_new':
        return '#28a745'; // Verde
      case 'preticket_item':
        return '#17a2b8'; // Azul claro para pré-tickets individuais
      default:
        return '#6c757d'; // Cinza
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));

    if (diffInMinutes < 1) {
      return 'Agora';
    }
    if (diffInMinutes < 60) {
      return `${diffInMinutes}m atrás`;
    }
    if (diffInMinutes < 1440) {
      const hours = Math.floor(diffInMinutes / 60);
      return `${hours}h atrás`;
    }
    const days = Math.floor(diffInMinutes / 1440);
    return `${days}d atrás`;
  };

  const handleNotificationClick = (notification) => {
    // Marcar como lida
    if (!notification.read) {
      handleMarkAsRead(notification.id);
    }

    // Navegar baseado no tipo de notificação
    if (
      notification.type === 'preticket_new' ||
      notification.type === 'preticket_item'
    ) {
      // Navegar para a página de pré-tickets
      console.log('Navegar para pré-tickets');
      window.location.href = '/pretickets';
    } else if (notification.data && notification.data.ticketId) {
      // Navegar para o ticket específico
      console.log('Navegar para ticket:', notification.data.ticketId);
      // window.location.href = `/tickets/${notification.data.ticketId}`;
    }
  };

  return (
    <div className="notification-center">
      <Dropdown show={isOpen} onToggle={toggle}>
        <Dropdown.Toggle
          as="button"
          className="btn btn-link notification-toggle"
          aria-label="Notificações"
        >
          <FaBell size={20} />
          {unreadCount > 0 && (
            <Badge bg="danger" className="notification-badge">
              {unreadCount > 99 ? '99+' : unreadCount}
            </Badge>
          )}
        </Dropdown.Toggle>

        <Dropdown.Menu align="end" className="notification-dropdown">
          <div className="notification-header">
            <h6 className="mb-0">Notificações</h6>
            {notifications.length > 0 && (
              <div className="notification-actions">
                {unreadCount > 0 && (
                  <Button
                    size="sm"
                    variant="link"
                    onClick={handleMarkAllAsRead}
                    title="Marcar todas como lidas"
                  >
                    <FaCheck size={12} />
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="link"
                  onClick={toggleMute}
                  title={muted ? 'Ativar som' : 'Silenciar som'}
                >
                  {muted ? <FaBellSlash size={12} /> : <FaBell size={12} />}
                </Button>
                <Button
                  size="sm"
                  variant="link"
                  onClick={handleClearAll}
                  title="Limpar todas"
                >
                  <FaTrash size={12} />
                </Button>
              </div>
            )}
          </div>

          <div className="notification-list">
            {notifications.length === 0 ? (
              <Dropdown.Item disabled>
                <div className="text-center text-muted py-3">
                  <FaBell size={24} className="mb-2" />
                  <br />
                  Nenhuma notificação
                </div>
              </Dropdown.Item>
            ) : (
              notifications.slice(0, 10).map((notification) => (
                <Dropdown.Item
                  key={notification.id}
                  className={`notification-item ${
                    !notification.read ? 'unread' : ''
                  }`}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <div className="notification-content">
                    <div className="notification-icon">
                      {getNotificationIcon(notification.type)}
                    </div>
                    <div className="notification-body">
                      <div className="notification-title">
                        {notification.title}
                      </div>
                      <div className="notification-message">
                        {notification.message}
                      </div>
                      <div className="notification-time">
                        {formatTimestamp(notification.timestamp)}
                      </div>
                    </div>
                    {!notification.read && (
                      <div className="notification-unread-indicator" />
                    )}
                  </div>
                </Dropdown.Item>
              ))
            )}
          </div>

          {notifications.length > 10 && <Dropdown.Divider />}
          {notifications.length > 10 && (
            <Dropdown.Item className="text-center">
              <small className="text-muted">
                Mostrando 10 de {notifications.length} notificações
              </small>
            </Dropdown.Item>
          )}
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );
}

export default NotificationCenter;
